import { useMemo, useState } from 'react';
import { Search, Plus, ChevronUp, ChevronDown, ChevronsUpDown, Check, Minus } from 'lucide-react';

const SortIcon = ({ dir }) => {
  if (dir === 'asc') return <ChevronUp size={11} className="text-slate-700" />;
  if (dir === 'desc') return <ChevronDown size={11} className="text-slate-700" />;
  return <ChevronsUpDown size={11} className="text-slate-400" />;
};

function fmtDate(s) {
  if (!s) return '—';
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function TemplatesTable({ templates, onAdd, onEdit }) {
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all'); // all | system | custom
  const [sortKey, setSortKey] = useState('type');
  const [sortDir, setSortDir] = useState('asc');

  const visible = useMemo(() => {
    let arr = templates.slice();
    if (typeFilter === 'system') arr = arr.filter(t => t.is_system);
    if (typeFilter === 'custom') arr = arr.filter(t => !t.is_system);
    if (search.trim()) {
      const s = search.trim().toLowerCase();
      arr = arr.filter(t =>
        (t.name || '').toLowerCase().includes(s) ||
        ((t.subjects || []).join(' ')).toLowerCase().includes(s)
      );
    }
    const getKey = {
      name:    t => (t.name || '').toLowerCase(),
      type:    t => (t.is_system ? 0 : 1),
      visible: t => (t.interviewer_visible ? 1 : 0),
      updated: t => t.updated_at || t.created_at || '',
      status:  t => (t.active === false ? 1 : 0),
    }[sortKey] || (t => (t.name || '').toLowerCase());
    arr.sort((a, b) => {
      const va = getKey(a); const vb = getKey(b);
      // For "type" ascending, system (0) comes first then name as tiebreaker
      if (va === vb && sortKey === 'type') {
        const na = (a.name || '').toLowerCase();
        const nb = (b.name || '').toLowerCase();
        return na < nb ? -1 : na > nb ? 1 : 0;
      }
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return arr;
  }, [templates, search, typeFilter, sortKey, sortDir]);

  const toggleSort = (k) => {
    if (sortKey === k) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(k); setSortDir('asc'); }
  };

  return (
    <div className="px-6 py-5" data-testid="templates-table">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <h2 className="text-[14px] font-semibold text-slate-900">Email templates</h2>
          <p className="text-[12px] text-slate-500 mt-0.5">System and custom email templates. Edits apply on the next send.</p>
        </div>
        <button
          onClick={onAdd}
          data-testid="add-template-button"
          className="h-8 flex items-center gap-1.5 bg-slate-900 hover:bg-slate-800 text-white px-3 rounded-md text-[13px] font-medium shadow-sm transition-colors"
        >
          <Plus size={13} /> New template
        </button>
      </div>

      {/* Filter bar */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-4">
        <div className="relative flex-1 max-w-xs">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by name or subject…"
            data-testid="search-templates"
            className="w-full h-8 bg-white border border-slate-200 focus:border-slate-900 focus:ring-1 focus:ring-slate-900 rounded-md pl-8 pr-2.5 text-[13px] text-slate-900 outline-none placeholder:text-slate-400 transition-colors"
          />
        </div>
        <select
          value={typeFilter}
          onChange={e => setTypeFilter(e.target.value)}
          data-testid="filter-template-type"
          className="h-8 bg-white border border-slate-200 focus:border-slate-900 focus:ring-1 focus:ring-slate-900 rounded-md px-2 text-[13px] text-slate-700 outline-none transition-colors"
        >
          <option value="all">All types</option>
          <option value="system">System</option>
          <option value="custom">Custom</option>
        </select>
        <span className="text-[12px] text-slate-500 sm:ml-1" data-testid="templates-count">{visible.length} template{visible.length === 1 ? '' : 's'}</span>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden shadow-sm">
        {visible.length === 0 ? (
          <div className="text-center py-16 px-6">
            <p className="text-[14px] text-slate-700 font-medium mb-1">No templates match the current filters</p>
            <p className="text-[12px] text-slate-500">Try clearing filters or create a new template.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead className="bg-slate-50/70 border-b border-slate-200">
                <tr className="text-[11px] font-medium text-slate-500 uppercase tracking-wider">
                  {[
                    { key: 'name',    label: 'Name',                    w: 'w-[24%]' },
                    { key: 'type',    label: 'Type',                    w: 'w-[10%]' },
                    { key: null,      label: 'Subject',                 w: 'w-[28%]' },
                    { key: 'visible', label: 'Visible to interviewer',  w: 'w-[14%]' },
                    { key: 'updated', label: 'Last edited',             w: 'w-[12%]' },
                    { key: 'status',  label: 'Status',                  w: 'w-[8%]'  },
                    { key: null,      label: '',                        w: 'w-[4%]'  },
                  ].map((col, i) => (
                    <th
                      key={i}
                      data-sortable={col.key ? 'true' : undefined}
                      data-testid={col.key ? `templates-sort-${col.key}` : undefined}
                      onClick={col.key ? () => toggleSort(col.key) : undefined}
                      className={`${col.w} px-4 py-2.5`}
                    >
                      <span className={`inline-flex items-center gap-1 ${col.key ? 'hover:text-slate-900' : ''}`}>
                        {col.label}
                        {col.key && <SortIcon dir={sortKey === col.key ? sortDir : null} />}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="text-[13px] text-slate-700">
                {visible.map(t => {
                  const subj = (t.subjects && t.subjects[0]) || '';
                  const subjLabel = (t.subjects && t.subjects.length > 1)
                    ? `${subj.slice(0, 40)}${subj.length > 40 ? '…' : ''} · ${t.subjects.length} variants`
                    : (subj.length > 70 ? subj.slice(0, 70) + '…' : (subj || '—'));
                  const ts = t.updated_at || t.created_at;
                  return (
                    <tr
                      key={t.id}
                      data-testid={`template-row-${t.key || t.id}`}
                      className="border-b border-slate-100 last:border-b-0 hover:bg-slate-50 transition-colors"
                    >
                      <td className="px-4 py-2.5">
                        <span className="font-medium text-slate-900">{t.name}</span>
                        {t.key === 'post_discovery_followup' && (
                          <span className="ops-pill bg-blue-50 text-blue-700 border-blue-100 ml-1.5">Advanced</span>
                        )}
                      </td>
                      <td className="px-4 py-2.5">
                        <span className={`ops-pill ${t.is_system ? 'bg-slate-100 text-slate-600 border-slate-200' : 'bg-emerald-50 text-emerald-700 border-emerald-100'}`}>
                          {t.is_system ? 'System' : 'Custom'}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-slate-600 truncate max-w-0">
                        <span className="truncate block" title={subj}>{subjLabel}</span>
                      </td>
                      <td className="px-4 py-2.5">
                        {t.interviewer_visible ? (
                          <span className="ops-pill bg-emerald-50 text-emerald-700 border-emerald-100">
                            <Check size={10} /> Yes
                          </span>
                        ) : (
                          <span className="ops-pill bg-slate-100 text-slate-500 border-slate-200">
                            <Minus size={10} /> No
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-2.5 text-slate-500 whitespace-nowrap">{fmtDate(ts)}</td>
                      <td className="px-4 py-2.5">
                        {t.active === false ? (
                          <span className="ops-pill bg-red-50 text-red-700 border-red-100">Inactive</span>
                        ) : (
                          <span className="ops-pill bg-slate-100 text-slate-600 border-slate-200">Active</span>
                        )}
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        <button
                          onClick={() => onEdit(t)}
                          data-testid={`edit-template-${t.key || t.id}`}
                          className="text-[12px] text-slate-700 hover:text-slate-900 hover:underline font-medium"
                        >
                          Edit
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
